import React, {Component} from "react"
import {observer} from "mobx-react"
import {observable} from "mobx"
import {Model} from "./Model"
import {Gate} from "./lib/gate/Gate"
import {Header} from "components/Header"
import {Footer} from "components/Footer"
import {Form} from "components/Form"
import {Loader} from "components/Loader"
import styles from "./App.module.css"
import {NavigationButton} from "components/NavigationButton"
import {FormID} from "./interfaces"
import {AdaptiveNavigation} from "./components/AdaptiveNavigation"
import {SignaturePopup} from "./components/SignaturePopup"
import {getParameterByName} from "./lib/functions/getParameterByName"
import {ALL_FORMS_IN_TABS, SAVE_CHANGES_MESSAGE} from "helper/Methods"

interface Props {
    model: Model
    gate: Gate
}

@observer
export class App extends Component<Props, {}> {
    @observable
    private isLoading = false

    @observable
    private navigation: "open" | "closed" = "open"

    @observable
    private isDesktopScreen: boolean =
        typeof window !== "undefined" ? window.innerWidth >= 1400 : true

    @observable
    private isDrawerOpen: boolean = false

    constructor(props) {
        super(props)

        this.onModel = this.onModel.bind(this)
        this.onResize = this.onResize.bind(this)
        props.model.dispatcher.addListener(this.onModel)
    }

    private onResize() {
        const isDesktop = window.innerWidth >= 1400
        this.isDesktopScreen = isDesktop
        if (isDesktop) {
            this.isDrawerOpen = false
        }
    }

    private onModel() {
        this.forceUpdate()
    }

    private onChangeNavigationState() {
        if (!this.isDesktopScreen) {
            this.isDrawerOpen = !this.isDrawerOpen
        }
    }
    public render() {
        const {model, gate} = this.props

        return (
            <React.Fragment>
                <Loader isVisible={gate.getLoading()} />
                {model.signaturePopup.show && (
                    <SignaturePopup
                        mode={model.signaturePopup.mode}
                        signature_key={model.signaturePopup.signature_key}
                        gate={gate}
                        model={model}
                        onClose={() => {
                            model.signaturePopup = {
                                show: false,
                                signature_key: "",
                                mode: "signature"
                            }
                        }}
                    />
                )}
                <div className={styles.root}>
                    <Header
                        onClickNavigation={() => {
                            this.onChangeNavigationState()
                        }}
                        model={model}
                    />

                    {/* Desktop Persistent Sidebar (>= 1400px) */}
                    <div className={styles.navigation}>
                        <AdaptiveNavigation
                            enabledItems={model.enabledItems}
                            checkedItems={
                                (Object.keys(model.document?.json_data?.forms || {}) as FormID[]) ||
                                []
                            }
                            selected={model.form.form_id}
                            onChange={(id: FormID) => {
                                if (ALL_FORMS_IN_TABS.includes(id)) {
                                    model.setFormId(id)
                                }
                            }}
                            model={model}
                        />
                    </div>

                    {/* Responsive Left Sidebar Drawer (only for mobile, tablet, ipad: < 1400px) */}
                    {!this.isDesktopScreen && this.isDrawerOpen && (
                        <React.Fragment>
                            <div
                                className={styles.drawerOverlay}
                                onClick={() => {
                                    this.isDrawerOpen = false
                                }}
                            />
                            <aside className={styles.drawer}>
                                <div className={styles.drawerHeader}>
                                    <div className={styles.drawerTitleWrapper}>
                                        <span className={styles.drawerTitle}>
                                            Application Steps
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.drawerCloseBtn}
                                        aria-label="Close navigation"
                                        onClick={() => {
                                            this.isDrawerOpen = false
                                        }}>
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </div>
                                <div className={styles.drawerContent}>
                                    <AdaptiveNavigation
                                        isDrawer={true}
                                        enabledItems={model.enabledItems}
                                        checkedItems={
                                            (Object.keys(
                                                model.document?.json_data?.forms || {}
                                            ) as FormID[]) || []
                                        }
                                        selected={model.form.form_id}
                                        onChange={(id: FormID) => {
                                            if (id && ALL_FORMS_IN_TABS.includes(id)) {
                                                this.isDrawerOpen = false
                                                model.setFormId(id)
                                            }
                                        }}
                                        model={model}
                                    />
                                </div>
                            </aside>
                        </React.Fragment>
                    )}

                    <main className={styles.main}>
                        <Form model={model} />
                    </main>
                    <NavigationButton
                        model={model}
                        className={styles.navigationButton}
                        setTabForms={() => this.forceUpdate()}
                    />
                    <Footer />
                </div>
            </React.Fragment>
        )
    }

    public async componentDidMount() {
        window.addEventListener("resize", this.onResize)
        this.onResize()

        this.isLoading = true
        const token = getParameterByName("token")
        const user_id = parseInt(getParameterByName("user_id"))
        await this.props.model.loadAll(token, user_id)
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.onResize)
    }
}
